import {
  RawMtfDailyRecord,
  DailyMtfSummary,
  StockMtfItem,
  MtfDailyDateSnapshot,
  MtfSizeHistoryPoint,
  MtfCompanySizeBreakdown,
  MtfPeriodicChange,
  MtfWeeklyRollup,
  MtfMonthlyRollup,
} from '../types';

const MTF_DAILY_TOTALS_URL = 'https://mtf.trading/mtf_daily_totals.json';
const MTF_SECTOR_PAGES_URL = 'https://mtf.trading/compressed_data/sector_pages.json';
const MTF_SCREENER_DATA_URL = 'https://mtf.trading/compressed_data/screener_data.json';
const MTF_SIZE_WEEKLY_URL = 'https://tipsheet.markets/data/v1/leverage/mtf/size_weekly.94f24cf6e842.json';
const MTF_SIZE_WEEKLY_FALLBACK_URL = '/mtf_size_weekly.json';
const getMtfDateUrl = (date: string) => `https://mtf.trading/date/${date}.json`;
const SESSION_CACHE_KEY = 'niftysutra.mtf-daily-totals.v3';
const SIZE_WEEKLY_CACHE_KEY = 'niftysutra.mtf-size-weekly.v2';
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes (reduced from 1h for timely refresh)

let memoryCache: DailyMtfSummary[] | null = null;
let pendingFetch: Promise<DailyMtfSummary[]> | null = null;
let stockOverviewCache: Record<string, StockMtfItem> | null = null;
let pendingStockOverviewFetch: Promise<Record<string, StockMtfItem>> | null = null;
let sizeHistoryCache: MtfSizeHistoryPoint[] | null = null;
let pendingSizeHistoryFetch: Promise<MtfSizeHistoryPoint[]> | null = null;
let companySizeHistoryCache: MtfSizeHistoryPoint[] | null = null;
let pendingCompanySizeHistoryFetch: Promise<MtfSizeHistoryPoint[]> | null = null;
const snapshotCache = new Map<string, MtfDailyDateSnapshot>();
const pendingSnapshotFetches = new Map<string, Promise<MtfDailyDateSnapshot | null>>();
let rawSectorPagesCache: any = null;
let pendingRawSectorPagesFetch: Promise<any> | null = null;
let rawScreenerDataCache: any = null;
let pendingRawScreenerDataFetch: Promise<any> | null = null;

/**
 * Clears all in-memory and sessionStorage caches for MTF data
 */
export const clearMtfCache = (): void => {
  memoryCache = null;
  stockOverviewCache = null;
  sizeHistoryCache = null;
  companySizeHistoryCache = null;
  pendingCompanySizeHistoryFetch = null;
  snapshotCache.clear();
  pendingSnapshotFetches.clear();
  rawSectorPagesCache = null;
  pendingRawSectorPagesFetch = null;
  rawScreenerDataCache = null;
  pendingRawScreenerDataFetch = null;
  if (typeof window !== 'undefined' && window.sessionStorage) {
    try {
      window.sessionStorage.removeItem(SESSION_CACHE_KEY);
      window.sessionStorage.removeItem('niftysutra.mtf-daily-totals.v1');
      window.sessionStorage.removeItem('niftysutra.mtf-daily-totals.v2');
      window.sessionStorage.removeItem(SIZE_WEEKLY_CACHE_KEY);
      window.sessionStorage.removeItem('niftysutra.mtf-size-weekly.v1');
    } catch {
      // Ignore storage errors
    }
  }
};

/**
 * Formats an amount in Crores into both short (Lakh Crores) and full Indian formatted strings.
 * e.g. 157486.1 -> { short: "₹1.57L Cr", full: "₹1,57,486 Cr", numericCr: 157486.1 }
 *      7358.0   -> { short: "₹7,358 Cr", full: "₹7,358 Cr", numericCr: 7358.0 }
 */
export const formatMtfCrores = (
  cr: number
): { short: string; full: string; numericCr: number } => {
  const rounded = Math.round(cr);
  const formattedCrores = `₹${rounded.toLocaleString('en-IN')} Cr`;

  if (Math.abs(cr) >= 100000) {
    const lakhCr = (cr / 100000).toFixed(2);
    return {
      short: `₹${lakhCr}L Cr`,
      full: formattedCrores,
      numericCr: cr,
    };
  }

  return {
    short: formattedCrores,
    full: formattedCrores,
    numericCr: cr,
  };
};

/**
 * Formats signed delta change string.
 * e.g. dayChangeCr = 1542.98, pct = 0.99 -> "+₹1,543 Cr (+0.99%)"
 */
export const formatMtfDayChange = (deltaCr: number, deltaPct: number): string => {
  const isNeg = deltaCr < 0;
  const absRoundedCr = Math.round(Math.abs(deltaCr));
  const sign = isNeg ? '-' : '+';
  const pctSign = deltaPct >= 0 ? '+' : '';
  return `${sign}₹${absRoundedCr.toLocaleString('en-IN')} Cr (${pctSign}${deltaPct.toFixed(2)}%)`;
};

/**
 * Transforms raw exchange MTF records into daily combined summaries.
 * Converts raw ₹ Lakhs to ₹ Crores (/ 100).
 */
export const parseMtfDailyTotals = (rawItems: RawMtfDailyRecord[]): DailyMtfSummary[] => {
  if (!Array.isArray(rawItems) || rawItems.length === 0) {
    return [];
  }

  // 1. Group records by ISO date YYYY-MM-DD
  const dateMap = new Map<string, { nse?: RawMtfDailyRecord; bse?: RawMtfDailyRecord }>();
  for (const item of rawItems) {
    if (!item?.date) continue;
    const existing = dateMap.get(item.date) || {};
    const ex = (item.exchange || '').toUpperCase();
    if (ex === 'NSE') {
      existing.nse = item;
    } else if (ex === 'BSE') {
      existing.bse = item;
    }
    dateMap.set(item.date, existing);
  }

  // 2. Sort dates chronologically ascending
  const sortedDates = Array.from(dateMap.keys()).sort((a, b) => a.localeCompare(b));

  const summaries: DailyMtfSummary[] = [];
  let runningMaxBookCr = 0;

  for (let i = 0; i < sortedDates.length; i++) {
    const date = sortedDates[i];
    const { nse, bse } = dateMap.get(date)!;

    // Convert raw Lakhs to Crores
    const nseBookCr = ((nse?.end_outstanding ?? nse?.amount_financed ?? 0) / 100);
    const bseBookCr = ((bse?.end_outstanding ?? bse?.amount_financed ?? 0) / 100);

    const prevSummary = summaries.length > 0 ? summaries[summaries.length - 1] : null;
    // Exclude trailing incomplete day (e.g. weekend or pending session where only BSE reported and NSE is still pending)
    // to avoid displaying a distorted -95% drop on incomplete data.
    if (!nse && prevSummary && prevSummary.nseBookCr > 1000 && i === sortedDates.length - 1) {
      continue;
    }

    const totalBookCr = nseBookCr + bseBookCr;

    const nseCount = nse?.securities_count ?? 0;
    const bseCount = bse?.securities_count ?? 0;
    const totalSecuritiesCount = nseCount + bseCount;

    const freshNse = (nse?.fresh_exposure ?? 0) / 100;
    const freshBse = (bse?.exposure_taken ?? bse?.fresh_exposure ?? 0) / 100;
    const freshExposureCr = freshNse + freshBse;

    const liqNse = (nse?.exposure_liquidated ?? 0) / 100;
    const liqBse = (bse?.exposure_liquidated ?? 0) / 100;
    const liquidatedExposureCr = liqNse + liqBse;

    const netFlowCr = freshExposureCr - liquidatedExposureCr;

    const dayChangeCr = prevSummary ? totalBookCr - prevSummary.totalBookCr : 0;
    const dayChangePct =
      prevSummary && prevSummary.totalBookCr > 0
        ? (dayChangeCr / prevSummary.totalBookCr) * 100
        : 0;

    const isRecordHigh = totalBookCr >= runningMaxBookCr;
    if (totalBookCr > runningMaxBookCr) {
      runningMaxBookCr = totalBookCr;
    }

    const { short, full } = formatMtfCrores(totalBookCr);
    const displayDayChange = formatMtfDayChange(dayChangeCr, dayChangePct);

    summaries.push({
      date,
      totalBookCr,
      nseBookCr,
      bseBookCr,
      totalSecuritiesCount,
      nseSecuritiesCount: nseCount,
      bseSecuritiesCount: bseCount,
      freshExposureCr,
      liquidatedExposureCr,
      netFlowCr,
      dayChangeCr,
      dayChangePct,
      displayBook: short,
      displayDetail: full,
      displayDayChange,
      isRecordHigh,
    });
  }

  return summaries;
};

/**
 * Fetches the complete market-wide MTF daily totals.
 * Uses in-memory singleton cache and sessionStorage.
 */
export const fetchMarketMtfTotals = async (options: { forceFresh?: boolean } = {}): Promise<DailyMtfSummary[]> => {
  if (!options.forceFresh && memoryCache && memoryCache.length > 0) {
    return memoryCache;
  }

  // Check sessionStorage if available
  if (!options.forceFresh && typeof window !== 'undefined' && window.sessionStorage) {
    try {
      const rawStored = window.sessionStorage.getItem(SESSION_CACHE_KEY);
      if (rawStored) {
        const parsed = JSON.parse(rawStored);
        if (parsed?.timestamp && Date.now() - parsed.timestamp < CACHE_TTL_MS && Array.isArray(parsed?.data)) {
          memoryCache = parsed.data;
          return parsed.data;
        }
      }
    } catch {
      // Ignore storage read errors
    }
  }

  if (pendingFetch) {
    return pendingFetch;
  }

  pendingFetch = (async () => {
    try {
      const fetchUrl = options.forceFresh
        ? `${MTF_DAILY_TOTALS_URL}?fresh=true&t=${Date.now()}`
        : MTF_DAILY_TOTALS_URL;

      const response = await fetch(fetchUrl, {
        headers: {
          Accept: 'application/json',
        },
        cache: options.forceFresh ? 'no-cache' : 'default',
      });

      if (!response.ok) {
        throw new Error(`Failed to load MTF daily totals: HTTP ${response.status}`);
      }

      const rawData: RawMtfDailyRecord[] = await response.json();
      const processed = parseMtfDailyTotals(rawData);

      memoryCache = processed;

      // Save to sessionStorage
      if (typeof window !== 'undefined' && window.sessionStorage) {
        try {
          window.sessionStorage.setItem(
            SESSION_CACHE_KEY,
            JSON.stringify({
              timestamp: Date.now(),
              data: processed,
            })
          );
        } catch {
          // Ignore storage quota exceeded
        }
      }

      return processed;
    } catch (err) {
      console.warn('[mtfService] Failed to fetch MTF totals:', err);
      // Return memory cache fallback if available
      if (memoryCache && memoryCache.length > 0) {
        return memoryCache;
      }
      throw err;
    } finally {
      pendingFetch = null;
    }
  })();

  return pendingFetch;
};

/**
 * Finds the MTF summary record for a specific date (YYYY-MM-DD).
 * If the exact date is not present (e.g. weekend/holiday or recent pending session),
 * finds the closest prior trading day available.
 */
export const getMtfRecordForDate = (
  summaries: DailyMtfSummary[],
  targetDate?: string | null
): DailyMtfSummary | null => {
  if (!Array.isArray(summaries) || summaries.length === 0) {
    return null;
  }

  if (!targetDate) {
    return summaries[summaries.length - 1];
  }

  const clean = targetDate.slice(0, 10);

  // Exact match
  const exact = summaries.find((s) => s.date === clean);
  if (exact) {
    return exact;
  }

  // Find closest prior date (date <= clean)
  for (let i = summaries.length - 1; i >= 0; i--) {
    if (summaries[i].date <= clean) {
      return summaries[i];
    }
  }

  // Fallback to earliest or latest
  return summaries[summaries.length - 1];
};

/**
 * Returns a window of historical summaries (e.g. last 30, 60, 90 days, or up to selectedDate).
 */
export const getMtfHistoryWindow = (
  summaries: DailyMtfSummary[],
  limitDays: number = 30,
  upToDate?: string | null
): DailyMtfSummary[] => {
  if (!Array.isArray(summaries) || summaries.length === 0) {
    return [];
  }

  let filtered = summaries;
  if (upToDate) {
    const clean = upToDate.slice(0, 10);
    filtered = summaries.filter((s) => s.date <= clean);
  }

  if (limitDays <= 0 || limitDays >= filtered.length) {
    return filtered;
  }

  return filtered.slice(-limitDays);
};

/**
 * Calculates Week-on-Week (WoW) and Month-on-Month (MoM) MTF leverage behavior.
 * Computes nominal ₹ Cr deltas, percentage changes, and whether the movement was positive (+) or negative (-).
 */
export const calculateMtfPeriodicChanges = (
  history: DailyMtfSummary[],
  targetRecord?: DailyMtfSummary | null
): {
  wow: MtfPeriodicChange | null;
  momCalendar: MtfPeriodicChange | null;
  momRolling: MtfPeriodicChange | null;
} => {
  if (!Array.isArray(history) || history.length === 0) {
    return { wow: null, momCalendar: null, momRolling: null };
  }

  const current = targetRecord || history[history.length - 1];
  if (!current) {
    return { wow: null, momCalendar: null, momRolling: null };
  }

  const currentIndex = history.findIndex((h) => h.date === current.date);
  if (currentIndex < 0) {
    return { wow: null, momCalendar: null, momRolling: null };
  }

  // 1. Week-on-Week (WoW):
  // Compare to ~5 trading days prior, or ~7 calendar days prior (closest trading day)
  let wowRef: DailyMtfSummary | null = null;
  if (currentIndex >= 5) {
    const currTime = new Date(current.date).getTime();
    let bestDiff = Infinity;
    for (let i = currentIndex - 1; i >= Math.max(0, currentIndex - 10); i--) {
      const candidateTime = new Date(history[i].date).getTime();
      const dayDiff = Math.round((currTime - candidateTime) / (1000 * 60 * 60 * 24));
      const distanceTo7Days = Math.abs(dayDiff - 7);
      if (distanceTo7Days < bestDiff && dayDiff >= 5) {
        bestDiff = distanceTo7Days;
        wowRef = history[i];
      }
    }
    if (!wowRef) {
      wowRef = history[currentIndex - 5];
    }
  } else if (currentIndex > 0) {
    wowRef = history[0];
  }

  let wow: MtfPeriodicChange | null = null;
  if (wowRef && wowRef.totalBookCr > 0) {
    const diff = current.totalBookCr - wowRef.totalBookCr;
    const pct = (diff / wowRef.totalBookCr) * 100;
    const isPos = diff >= 0;
    const sign = diff > 0 ? '+' : diff < 0 ? '-' : '';
    const signPct = pct > 0 ? '+' : '';
    wow = {
      periodLabel: 'Week-on-Week',
      currentDate: current.date,
      referenceDate: wowRef.date,
      currentBookCr: current.totalBookCr,
      referenceBookCr: wowRef.totalBookCr,
      changeCr: diff,
      changePct: pct,
      isPositive: isPos,
      displayText: `${sign}₹${Math.abs(Math.round(diff)).toLocaleString('en-IN')} Cr (${signPct}${pct.toFixed(2)}%)`,
    };
  }

  // 2. Month-on-Month Calendar (MTD vs Previous Month Close)
  const currentMonth = current.date.slice(0, 7);
  let prevMonthCloseRef: DailyMtfSummary | null = null;
  for (let i = currentIndex - 1; i >= 0; i--) {
    if (history[i].date.slice(0, 7) < currentMonth) {
      prevMonthCloseRef = history[i];
      break;
    }
  }

  let momCalendar: MtfPeriodicChange | null = null;
  if (prevMonthCloseRef && prevMonthCloseRef.totalBookCr > 0) {
    const diff = current.totalBookCr - prevMonthCloseRef.totalBookCr;
    const pct = (diff / prevMonthCloseRef.totalBookCr) * 100;
    const isPos = diff >= 0;
    const sign = diff > 0 ? '+' : diff < 0 ? '-' : '';
    const signPct = pct > 0 ? '+' : '';
    momCalendar = {
      periodLabel: 'Month-to-Date (vs Prior Month Close)',
      currentDate: current.date,
      referenceDate: prevMonthCloseRef.date,
      currentBookCr: current.totalBookCr,
      referenceBookCr: prevMonthCloseRef.totalBookCr,
      changeCr: diff,
      changePct: pct,
      isPositive: isPos,
      displayText: `${sign}₹${Math.abs(Math.round(diff)).toLocaleString('en-IN')} Cr (${signPct}${pct.toFixed(2)}%)`,
    };
  }

  // 3. Rolling 30-Day MoM (~21 trading sessions prior)
  let rollingRef: DailyMtfSummary | null = null;
  if (currentIndex >= 21) {
    rollingRef = history[currentIndex - 21];
  } else if (currentIndex > 0) {
    rollingRef = history[0];
  }

  let momRolling: MtfPeriodicChange | null = null;
  if (rollingRef && rollingRef.totalBookCr > 0) {
    const diff = current.totalBookCr - rollingRef.totalBookCr;
    const pct = (diff / rollingRef.totalBookCr) * 100;
    const isPos = diff >= 0;
    const sign = diff > 0 ? '+' : diff < 0 ? '-' : '';
    const signPct = pct > 0 ? '+' : '';
    momRolling = {
      periodLabel: 'Rolling 30-Day',
      currentDate: current.date,
      referenceDate: rollingRef.date,
      currentBookCr: current.totalBookCr,
      referenceBookCr: rollingRef.totalBookCr,
      changeCr: diff,
      changePct: pct,
      isPositive: isPos,
      displayText: `${sign}₹${Math.abs(Math.round(diff)).toLocaleString('en-IN')} Cr (${signPct}${pct.toFixed(2)}%)`,
    };
  }

  return { wow, momCalendar, momRolling };
};

/**
 * Aggregates daily MTF summaries into calendar week rollups (Friday-based).
 * Returns weeks sorted descending (latest week first).
 */
export const aggregateMtfWeeklyRollups = (
  history: DailyMtfSummary[],
  limitWeeks: number = 24
): MtfWeeklyRollup[] => {
  if (!Array.isArray(history) || history.length === 0) {
    return [];
  }

  const weeksMap = new Map<string, DailyMtfSummary[]>();
  for (const item of history) {
    if (!item?.date) continue;
    const d = new Date(item.date);
    const day = d.getDay(); // 0 is Sun, 5 is Fri
    const diffDays = 5 - day;
    const fri = new Date(d);
    fri.setDate(d.getDate() + (day <= 5 ? diffDays : diffDays + 7));
    const friKey = fri.toISOString().slice(0, 10);

    let list = weeksMap.get(friKey);
    if (!list) {
      list = [];
      weeksMap.set(friKey, list);
    }
    list.push(item);
  }

  const sortedWeekKeys = Array.from(weeksMap.keys()).sort((a, b) => a.localeCompare(b));
  const rollups: MtfWeeklyRollup[] = [];

  for (let i = 0; i < sortedWeekKeys.length; i++) {
    const weekEndDate = sortedWeekKeys[i];
    const days = weeksMap.get(weekEndDate)!;
    const latestDay = days[days.length - 1];

    let changeCr = 0;
    let changePct = 0;
    if (i > 0) {
      const prevDays = weeksMap.get(sortedWeekKeys[i - 1])!;
      const prevLatest = prevDays[prevDays.length - 1];
      if (prevLatest.totalBookCr > 0) {
        changeCr = latestDay.totalBookCr - prevLatest.totalBookCr;
        changePct = (changeCr / prevLatest.totalBookCr) * 100;
      }
    } else {
      changeCr = latestDay.dayChangeCr;
      changePct = latestDay.dayChangePct;
    }

    const totalFresh = days.reduce((sum, d) => sum + (d.freshExposureCr || 0), 0);
    const totalLiq = days.reduce((sum, d) => sum + (d.liquidatedExposureCr || 0), 0);
    const isPos = changeCr >= 0;
    const sign = changeCr > 0 ? '+' : changeCr < 0 ? '-' : '';
    const signPct = changePct > 0 ? '+' : '';

    rollups.push({
      weekEndDate,
      asOfDate: latestDay.date,
      totalBookCr: latestDay.totalBookCr,
      nseBookCr: latestDay.nseBookCr,
      bseBookCr: latestDay.bseBookCr,
      changeCr,
      changePct,
      isPositive: isPos,
      freshExposureCr: totalFresh,
      liquidatedExposureCr: totalLiq,
      netFlowCr: totalFresh - totalLiq,
      displayBook: latestDay.displayBook,
      displayChange: `${sign}₹${Math.abs(Math.round(changeCr)).toLocaleString('en-IN')} Cr (${signPct}${changePct.toFixed(2)}%)`,
    });
  }

  const result = rollups.reverse();
  return limitWeeks > 0 ? result.slice(0, limitWeeks) : result;
};

/**
 * Aggregates daily MTF summaries into calendar month rollups.
 * Returns months sorted descending (latest month first).
 */
export const aggregateMtfMonthlyRollups = (
  history: DailyMtfSummary[],
  limitMonths: number = 24
): MtfMonthlyRollup[] => {
  if (!Array.isArray(history) || history.length === 0) {
    return [];
  }

  const monthsMap = new Map<string, DailyMtfSummary[]>();
  for (const item of history) {
    if (!item?.date) continue;
    const monthKey = item.date.slice(0, 7);
    let list = monthsMap.get(monthKey);
    if (!list) {
      list = [];
      monthsMap.set(monthKey, list);
    }
    list.push(item);
  }

  const sortedMonthKeys = Array.from(monthsMap.keys()).sort((a, b) => a.localeCompare(b));
  const rollups: MtfMonthlyRollup[] = [];

  const MONTH_NAMES = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
  ];

  for (let i = 0; i < sortedMonthKeys.length; i++) {
    const monthKey = sortedMonthKeys[i];
    const days = monthsMap.get(monthKey)!;
    const firstDay = days[0];
    const lastDay = days[days.length - 1];

    let startBookCr = firstDay.totalBookCr;
    if (i > 0) {
      const prevDays = monthsMap.get(sortedMonthKeys[i - 1])!;
      startBookCr = prevDays[prevDays.length - 1].totalBookCr;
    }

    const endBookCr = lastDay.totalBookCr;
    const changeCr = endBookCr - startBookCr;
    const changePct = startBookCr > 0 ? (changeCr / startBookCr) * 100 : 0;
    const isPos = changeCr >= 0;
    const sign = changeCr > 0 ? '+' : changeCr < 0 ? '-' : '';
    const signPct = changePct > 0 ? '+' : '';

    const peakBookCr = Math.max(...days.map((d) => d.totalBookCr));
    const lowBookCr = Math.min(...days.map((d) => d.totalBookCr));

    const [yearStr, monthStr] = monthKey.split('-');
    const monthIndex = parseInt(monthStr, 10) - 1;
    const monthLabel = `${MONTH_NAMES[monthIndex] || monthStr} ${yearStr}`;

    rollups.push({
      monthKey,
      monthLabel,
      startBookCr,
      endBookCr,
      startDate: firstDay.date,
      endDate: lastDay.date,
      changeCr,
      changePct,
      isPositive: isPos,
      peakBookCr,
      lowBookCr,
      tradingDays: days.length,
      displayEndBook: lastDay.displayBook,
      displayChange: `${sign}₹${Math.abs(Math.round(changeCr)).toLocaleString('en-IN')} Cr (${signPct}${changePct.toFixed(2)}%)`,
    });
  }

  const result = rollups.reverse();
  return limitMonths > 0 ? result.slice(0, limitMonths) : result;
};

export const cleanMtfSymbol = (symbol: string): string => {
  return symbol.trim().toUpperCase().replace(/^(NSE|BSE):/, '');
};

export const parseStockMtfItem = (raw: any, asOfDate?: string): StockMtfItem => {
  const amtLakhs = Number(raw?.amt ?? raw?.latest_amount ?? 0);
  const amtCr = Math.round((amtLakhs / 100) * 100) / 100;
  const deltaLakhs = raw?.delta != null ? Number(raw.delta) : undefined;
  const deltaCr = deltaLakhs != null ? Math.round((deltaLakhs / 100) * 100) / 100 : undefined;
  const deltaPct = raw?.delta_pct != null ? Number(raw.delta_pct) : undefined;

  return {
    symbol: cleanMtfSymbol(raw?.symbol || ''),
    name: raw?.name || undefined,
    exchange: raw?.exchange || undefined,
    amtLakhs,
    amtCr,
    deltaLakhs,
    deltaCr,
    deltaPct,
    asOfDate: asOfDate || raw?.date_range?.end,
  };
};

export const parseMtfDateSnapshot = (raw: any): MtfDailyDateSnapshot => {
  const date = raw?.date || '';
  const mapItems = (items: any[]): StockMtfItem[] => {
    if (!Array.isArray(items)) return [];
    return items.map((item) => parseStockMtfItem(item, date));
  };

  const nseGainers = raw?.exchanges?.NSE ? mapItems(raw.exchanges.NSE.gainers) : [];
  const bseGainers = raw?.exchanges?.BSE ? mapItems(raw.exchanges.BSE.gainers) : [];
  const nseLosers = raw?.exchanges?.NSE ? mapItems(raw.exchanges.NSE.losers) : [];
  const bseLosers = raw?.exchanges?.BSE ? mapItems(raw.exchanges.BSE.losers) : [];
  const nseTop = raw?.exchanges?.NSE ? mapItems(raw.exchanges.NSE.top_funded) : [];
  const bseTop = raw?.exchanges?.BSE ? mapItems(raw.exchanges.BSE.top_funded) : [];

  const combineUnique = (listA: StockMtfItem[], listB: StockMtfItem[], sortByDeltaDesc = true): StockMtfItem[] => {
    const map = new Map<string, StockMtfItem>();
    for (const item of [...listA, ...listB]) {
      if (!map.has(item.symbol)) {
        map.set(item.symbol, item);
      } else {
        const ex = map.get(item.symbol)!;
        map.set(item.symbol, {
          ...ex,
          amtCr: Math.round((ex.amtCr + item.amtCr) * 100) / 100,
          amtLakhs: (ex.amtLakhs || 0) + (item.amtLakhs || 0),
          deltaCr:
            ex.deltaCr != null || item.deltaCr != null
              ? Math.round(((ex.deltaCr || 0) + (item.deltaCr || 0)) * 100) / 100
              : undefined,
        });
      }
    }
    const combined = Array.from(map.values());
    if (sortByDeltaDesc) {
      return combined.sort((a, b) => (b.deltaCr ?? 0) - (a.deltaCr ?? 0));
    }
    return combined.sort((a, b) => (a.deltaCr ?? 0) - (b.deltaCr ?? 0));
  };

  const gainers = combineUnique(nseGainers, bseGainers, true);
  const losers = combineUnique(nseLosers, bseLosers, false);
  const topFunded = [...nseTop, ...bseTop].sort((a, b) => b.amtCr - a.amtCr);

  return {
    date,
    label: raw?.label,
    narrative: raw?.narrative,
    headline: raw?.headline,
    composition: raw?.composition,
    gainers,
    losers,
    topFunded,
    nse: raw?.exchanges?.NSE ? {
      topFunded: nseTop,
      gainers: nseGainers,
      losers: nseLosers,
    } : undefined,
    bse: raw?.exchanges?.BSE ? {
      topFunded: bseTop,
      gainers: bseGainers,
      losers: bseLosers,
    } : undefined,
  };
};

export const fetchMtfDateSnapshot = async (
  targetDate?: string,
  options: { forceFresh?: boolean } = {}
): Promise<MtfDailyDateSnapshot | null> => {
  if (!targetDate) return null;
  const cleanDate = targetDate.slice(0, 10);

  if (!options.forceFresh && snapshotCache.has(cleanDate)) {
    return snapshotCache.get(cleanDate)!;
  }
  if (!options.forceFresh && pendingSnapshotFetches.has(cleanDate)) {
    return pendingSnapshotFetches.get(cleanDate)!;
  }

  const request = (async () => {
    try {
      const res = await fetch(getMtfDateUrl(cleanDate), {
        headers: { Accept: 'application/json' },
      });
      if (!res.ok) {
        return null;
      }
      const raw = await res.json();
      const snapshot = parseMtfDateSnapshot(raw);
      snapshotCache.set(cleanDate, snapshot);
      return snapshot;
    } catch (err) {
      console.warn(`[mtfService] Failed to fetch MTF date snapshot for ${cleanDate}:`, err);
      return null;
    }
  })();

  pendingSnapshotFetches.set(cleanDate, request);
  try {
    return await request;
  } finally {
    pendingSnapshotFetches.delete(cleanDate);
  }
};

export const fetchRawMtfSectorPages = async (options: { forceFresh?: boolean } = {}): Promise<any> => {
  if (!options.forceFresh && rawSectorPagesCache) {
    return rawSectorPagesCache;
  }
  if (!options.forceFresh && pendingRawSectorPagesFetch) {
    return pendingRawSectorPagesFetch;
  }

  pendingRawSectorPagesFetch = (async () => {
    try {
      const res = await fetch(MTF_SECTOR_PAGES_URL, { headers: { Accept: 'application/json' } });
      if (!res.ok) return null;
      const json = await res.json();
      rawSectorPagesCache = json;
      return json;
    } catch (err) {
      console.warn('[mtfService] Failed to fetch raw sector_pages json:', err);
      return rawSectorPagesCache;
    } finally {
      pendingRawSectorPagesFetch = null;
    }
  })();

  return pendingRawSectorPagesFetch;
};

export const fetchRawMtfScreenerData = async (options: { forceFresh?: boolean } = {}): Promise<any> => {
  if (!options.forceFresh && rawScreenerDataCache) {
    return rawScreenerDataCache;
  }
  if (!options.forceFresh && pendingRawScreenerDataFetch) {
    return pendingRawScreenerDataFetch;
  }

  pendingRawScreenerDataFetch = (async () => {
    try {
      const res = await fetch(MTF_SCREENER_DATA_URL, { headers: { Accept: 'application/json' } });
      if (!res.ok) return null;
      const json = await res.json();
      rawScreenerDataCache = json;
      return json;
    } catch (err) {
      console.warn('[mtfService] Failed to fetch raw screener_data json:', err);
      return rawScreenerDataCache;
    } finally {
      pendingRawScreenerDataFetch = null;
    }
  })();

  return pendingRawScreenerDataFetch;
};

export const fetchStockMtfOverview = async (
  options: { forceFresh?: boolean } = {}
): Promise<Record<string, StockMtfItem>> => {
  if (!options.forceFresh && stockOverviewCache && Object.keys(stockOverviewCache).length > 0) {
    return stockOverviewCache;
  }

  if (pendingStockOverviewFetch) {
    return pendingStockOverviewFetch;
  }

  pendingStockOverviewFetch = (async () => {
    try {
      // Concurrently fetch sector classifications and market-wide screener data (shared cache with mtfPositioningService)
      const [rawSector, rawScreener] = await Promise.all([
        fetchRawMtfSectorPages(options),
        fetchRawMtfScreenerData(options),
      ]);

      const map: Record<string, StockMtfItem> = {};
      let asOfDate: string | undefined = undefined;

      // 1. Ingest sector_pages if available
      if (rawSector && typeof rawSector === 'object') {
        try {
          const rawStocks = rawSector?.stocks || {};
          asOfDate = rawSector?.data_date || undefined;

          for (const [key, item] of Object.entries<any>(rawStocks)) {
            if (!key) continue;
            const symbol = cleanMtfSymbol(key);
            const amtLakhs = Number(item?.bk || 0);
            const amtCr = Math.round((amtLakhs / 100) * 100) / 100;
            const leveragePct = item?.lv != null ? Math.round(Number(item.lv) * 100) / 100 : undefined;
            const marketCapCr = item?.mc != null ? Math.round((Number(item.mc) / 100) * 100) / 100 : undefined;
            const freeFloatCr = item?.ff != null ? Math.round((Number(item.ff) / 100) * 100) / 100 : undefined;

            map[symbol] = {
              symbol,
              name: item?.n || undefined,
              exchange: item?.ex || 'NSE',
              amtLakhs,
              amtCr,
              leveragePct,
              marketCapCr,
              freeFloatCr,
              sector: item?.s || item?.m || undefined,
              industry: item?.i || undefined,
              daysToCover: item?.dtc != null ? Number(item.dtc) : undefined,
              asOfDate,
            };
          }
        } catch (err) {
          console.warn('[mtfService] Failed to parse sector_pages json:', err);
        }
      }

      // 2. Ingest screener_data to enrich all stocks with 1-day deltas, float leverage, and coverage metrics
      if (rawScreener && typeof rawScreener === 'object') {
        try {
          const allStocksList = Array.isArray(rawScreener?.all_stocks) ? rawScreener.all_stocks : [];

          for (const s of allStocksList) {
            if (!s?.symbol) continue;
            const symbol = cleanMtfSymbol(s.symbol);
            const dlt = s.dlt || {};
            const nseDlt = dlt.NSE?.['1'];
            const bseDlt = dlt.BSE?.['1'];
            const hasDelta = nseDlt != null || bseDlt != null;
            const deltaLakhs = (Number(nseDlt) || 0) + (Number(bseDlt) || 0);
            const deltaCr = Math.round((deltaLakhs / 100) * 100) / 100;

            const amtLakhs = Number(s.combined_book_lakhs || s.amount_financed || 0);
            const amtCr = Math.round((amtLakhs / 100) * 100) / 100;
            const prevAmtCr = amtCr - deltaCr;

            let deltaPct: number | undefined;
            if (hasDelta && Math.abs(deltaCr) > 0 && prevAmtCr > 0) {
              deltaPct = Math.round(((deltaCr / prevAmtCr) * 100) * 100) / 100;
            } else if (s.chg?.NSE?.['1'] != null) {
              deltaPct = Number(s.chg.NSE['1']);
            } else if (s.chg?.BSE?.['1'] != null) {
              deltaPct = Number(s.chg.BSE['1']);
            }

            const rawLev = s.ff_leverage_pct != null ? s.ff_leverage_pct : s.leverage_pct;
            const leveragePct = rawLev != null ? Math.round(Number(rawLev) * 100) / 100 : undefined;
            const marketCapCr = s.mcap_lakhs != null ? Math.round((Number(s.mcap_lakhs) / 100) * 100) / 100 : undefined;
            const freeFloatCr = s.ffmc_lakhs != null ? Math.round((Number(s.ffmc_lakhs) / 100) * 100) / 100 : undefined;
            const daysToCover = s.days_to_cover != null ? Number(s.days_to_cover) : undefined;
            const stockDate = s.date || asOfDate;

            const existing = map[symbol];
            map[symbol] = {
              symbol,
              name: s.name || existing?.name || undefined,
              exchange: s.exchange || existing?.exchange || 'NSE',
              amtLakhs: amtLakhs || existing?.amtLakhs || 0,
              amtCr: amtCr || existing?.amtCr || 0,
              deltaLakhs: hasDelta ? deltaLakhs : existing?.deltaLakhs,
              deltaCr: hasDelta ? deltaCr : existing?.deltaCr,
              deltaPct: hasDelta ? deltaPct : existing?.deltaPct,
              leveragePct: leveragePct ?? existing?.leveragePct,
              marketCapCr: marketCapCr ?? existing?.marketCapCr,
              freeFloatCr: freeFloatCr ?? existing?.freeFloatCr,
              sector: existing?.sector,
              industry: existing?.industry,
              daysToCover: daysToCover ?? existing?.daysToCover,
              asOfDate: stockDate || existing?.asOfDate,
            };
          }
        } catch (err) {
          console.warn('[mtfService] Failed to parse screener_data json:', err);
        }
      }

      // 3. Assign AMFI Market-Cap Rank & Tier (1-100 Large, 101-250 Mid, 251+ Small)
      const validMcapStocks = Object.values(map)
        .filter((s) => (s.marketCapCr ?? 0) > 0)
        .sort((a, b) => (b.marketCapCr ?? 0) - (a.marketCapCr ?? 0));

      validMcapStocks.forEach((s, idx) => {
        const rank = idx + 1;
        const target = map[s.symbol];
        if (target) {
          target.marketCapRank = rank;
          if (rank <= 100) {
            target.capTier = 'large';
          } else if (rank <= 250) {
            target.capTier = 'mid';
          } else {
            target.capTier = 'small';
          }
        }
      });

      // Default any remaining unranked / micro stocks to small cap
      Object.values(map).forEach((s) => {
        if (!s.capTier) {
          s.capTier = 'small';
        }
      });

      if (Object.keys(map).length > 0) {
        stockOverviewCache = map;
      }
      return map;
    } catch (err) {
      console.warn('[mtfService] Failed to fetch MTF stock overview:', err);
      if (stockOverviewCache) return stockOverviewCache;
      return {};
    } finally {
      pendingStockOverviewFetch = null;
    }
  })();

  return pendingStockOverviewFetch;
};

export const fetchAllStockMtfList = async (
  options: { forceFresh?: boolean } = {}
): Promise<StockMtfItem[]> => {
  const map = await fetchStockMtfOverview(options);
  return Object.values(map).sort((a, b) => b.amtCr - a.amtCr);
};

export const getStockMtf = async (symbol?: string | null): Promise<StockMtfItem | null> => {
  if (!symbol) return null;
  const clean = cleanMtfSymbol(symbol);
  const overview = await fetchStockMtfOverview();
  return overview[clean] || null;
};

/**
 * Parses raw size_weekly json response into typed time-series array.
 */
export const parseMtfSizeWeekly = (raw: any): MtfSizeHistoryPoint[] => {
  if (!raw || !Array.isArray(raw.dates) || !raw.series) {
    return [];
  }
  const dates: string[] = raw.dates;
  const series = raw.series;
  const largeBook = series.large_book_pct || [];
  const midBook = series.mid_book_pct || [];
  const smallBook = series.small_book_pct || [];
  const largeMcap = series.large_mcap_pct || [];
  const midMcap = series.mid_mcap_pct || [];
  const smallMcap = series.small_mcap_pct || [];
  const crowded = series.crowded_book_pct || [];
  const largeBps = series.large_bps || [];
  const midBps = series.mid_bps || [];
  const smallBps = series.small_bps || [];

  return dates.map((date, idx) => ({
    date,
    largeBookPct: Math.round((Number(largeBook[idx]) || 0) * 100) / 100,
    midBookPct: Math.round((Number(midBook[idx]) || 0) * 100) / 100,
    smallBookPct: Math.round((Number(smallBook[idx]) || 0) * 100) / 100,
    largeMcapPct: largeMcap[idx] != null ? Math.round((Number(largeMcap[idx]) || 0) * 100) / 100 : undefined,
    midMcapPct: midMcap[idx] != null ? Math.round((Number(midMcap[idx]) || 0) * 100) / 100 : undefined,
    smallMcapPct: smallMcap[idx] != null ? Math.round((Number(smallMcap[idx]) || 0) * 100) / 100 : undefined,
    crowdedBookPct: crowded[idx] != null ? Math.round((Number(crowded[idx]) || 0) * 100) / 100 : undefined,
    largeBps: largeBps[idx] != null ? Number(largeBps[idx]) : undefined,
    midBps: midBps[idx] != null ? Number(midBps[idx]) : undefined,
    smallBps: smallBps[idx] != null ? Number(smallBps[idx]) : undefined,
  }));
};

/**
 * Fetches the historical weekly company size breakdown time-series (AMFI Large, Mid, Small).
 * Employs memory caching, sessionStorage, and automatic fallback to bundled /mtf_size_weekly.json.
 */
export const fetchMtfSizeWeekly = async (
  options: { forceFresh?: boolean } = {}
): Promise<MtfSizeHistoryPoint[]> => {
  if (!options.forceFresh && sizeHistoryCache && sizeHistoryCache.length > 0) {
    return sizeHistoryCache;
  }

  // Check sessionStorage
  if (!options.forceFresh && typeof window !== 'undefined' && window.sessionStorage) {
    try {
      const rawStored = window.sessionStorage.getItem(SIZE_WEEKLY_CACHE_KEY);
      if (rawStored) {
        const parsed = JSON.parse(rawStored);
        if (parsed?.timestamp && Date.now() - parsed.timestamp < CACHE_TTL_MS && Array.isArray(parsed?.data)) {
          sizeHistoryCache = parsed.data;
          return parsed.data;
        }
      }
    } catch {
      // Ignore storage errors
    }
  }

  if (pendingSizeHistoryFetch) {
    return pendingSizeHistoryFetch;
  }

  pendingSizeHistoryFetch = (async () => {
    let raw: any = null;

    // 1. Try remote primary endpoint
    try {
      const res = await fetch(MTF_SIZE_WEEKLY_URL, {
        headers: { Accept: 'application/json' },
      });
      if (res.ok) {
        raw = await res.json();
      }
    } catch {
      // Remote fetch failed, fall through to fallback
    }

    // 2. Try local fallback if remote failed
    if (!raw) {
      try {
        const fallbackRes = await fetch(MTF_SIZE_WEEKLY_FALLBACK_URL, {
          headers: { Accept: 'application/json' },
        });
        if (fallbackRes.ok) {
          raw = await fallbackRes.json();
        }
      } catch (err) {
        console.warn('[mtfService] Failed to load local size_weekly fallback:', err);
      }
    }

    if (!raw) {
      if (sizeHistoryCache && sizeHistoryCache.length > 0) {
        return sizeHistoryCache;
      }
      throw new Error('Failed to load MTF company size historical data');
    }

    const processed = parseMtfSizeWeekly(raw);
    sizeHistoryCache = processed;

    if (typeof window !== 'undefined' && window.sessionStorage) {
      try {
        window.sessionStorage.setItem(
          SIZE_WEEKLY_CACHE_KEY,
          JSON.stringify({
            timestamp: Date.now(),
            data: processed,
          })
        );
      } catch {
        // Ignore quota errors
      }
    }

    return processed;
  })().finally(() => {
    pendingSizeHistoryFetch = null;
  });

  return pendingSizeHistoryFetch;
};

export interface FetchMtfCompanySizeOptions {
  forceFresh?: boolean;
  stocks?: StockMtfItem[];
  dailyDates?: string[];
  targetDate?: string;
}

/**
 * Fetches the complete company size historical breakdown time-series, merging the
 * historical weekly series with daily session snapshots so the chart updates on a daily basis.
 */
export const fetchMtfCompanySizeHistory = async (
  options: FetchMtfCompanySizeOptions = {}
): Promise<MtfSizeHistoryPoint[]> => {
  if (!options.forceFresh && companySizeHistoryCache && companySizeHistoryCache.length > 0) {
    return companySizeHistoryCache;
  }
  if (!options.forceFresh && pendingCompanySizeHistoryFetch) {
    return pendingCompanySizeHistoryFetch;
  }

  const request = (async () => {
    const weeklyHistory = await fetchMtfSizeWeekly({ forceFresh: options.forceFresh });
    if (!weeklyHistory || weeklyHistory.length === 0) {
      return [];
    }

    const lastWeeklyDate = weeklyHistory[weeklyHistory.length - 1].date;

    // Collect candidate daily dates occurring after the weekly file's cutoff date
    const candidateDates = new Set<string>();

    if (Array.isArray(options.dailyDates)) {
      options.dailyDates.forEach((d) => {
        if (d && d > lastWeeklyDate) candidateDates.add(d.slice(0, 10));
      });
    }

    if (options.targetDate && options.targetDate > lastWeeklyDate) {
      candidateDates.add(options.targetDate.slice(0, 10));
    }

    if (memoryCache && Array.isArray(memoryCache)) {
      memoryCache.forEach((m) => {
        if (m.date && m.date > lastWeeklyDate) candidateDates.add(m.date);
      });
    }

    if (Array.isArray(options.stocks)) {
      options.stocks.forEach((s) => {
        if (s.asOfDate && s.asOfDate > lastWeeklyDate) {
          candidateDates.add(s.asOfDate.slice(0, 10));
        }
      });
    }

    // If candidateDates is empty, try to inspect daily totals to discover any newer trading sessions
    if (candidateDates.size === 0) {
      try {
        const dailyTotals = await fetchMarketMtfTotals({ forceFresh: options.forceFresh });
        if (Array.isArray(dailyTotals)) {
          dailyTotals.forEach((m) => {
            if (m.date && m.date > lastWeeklyDate) candidateDates.add(m.date);
          });
        }
      } catch {
        // Non-blocking fallback
      }
    }

    const sortedDailyDates = Array.from(candidateDates)
      .filter((d) => d > lastWeeklyDate)
      .sort((a, b) => a.localeCompare(b));

    if (sortedDailyDates.length === 0) {
      companySizeHistoryCache = weeklyHistory;
      return weeklyHistory;
    }

    // Clone weekly base history
    const merged: MtfSizeHistoryPoint[] = [...weeklyHistory];

    // Fetch daily date snapshots concurrently
    const snapshots = await Promise.all(
      sortedDailyDates.map((date) =>
        fetchMtfDateSnapshot(date, { forceFresh: options.forceFresh })
          .then((snap) => ({ date, snap }))
          .catch(() => ({ date, snap: null }))
      )
    );

    for (const { date, snap } of snapshots) {
      const prevPoint = merged[merged.length - 1];

      if (snap?.composition?.cap) {
        const cap = snap.composition.cap;
        const capLarge = cap.large || 0;
        const capMid = cap.mid || 0;
        const capSmall = cap.small || 0;
        const capTotal = cap.total || (capLarge + capMid + capSmall);

        const largeBookPct = capTotal > 0 ? Math.round((capLarge / capTotal) * 10000) / 100 : 0;
        const midBookPct = capTotal > 0 ? Math.round((capMid / capTotal) * 10000) / 100 : 0;
        const smallBookPct = capTotal > 0 ? Math.round((capSmall / capTotal) * 10000) / 100 : 0;

        const largeBookCr = Math.round((capLarge / 100) * 100) / 100;
        const midBookCr = Math.round((capMid / 100) * 100) / 100;
        const smallBookCr = Math.round((capSmall / 100) * 100) / 100;
        const totalBookCr = Math.round((capTotal / 100) * 100) / 100;

        merged.push({
          date,
          largeBookPct,
          midBookPct,
          smallBookPct,
          largeMcapPct: prevPoint?.largeMcapPct ?? 59.52,
          midMcapPct: prevPoint?.midMcapPct ?? 20.26,
          smallMcapPct: prevPoint?.smallMcapPct ?? 20.22,
          crowdedBookPct: prevPoint?.crowdedBookPct ?? 19.6,
          largeBookCr,
          midBookCr,
          smallBookCr,
          totalBookCr,
          isDaily: true,
        });
      } else if (Array.isArray(options.stocks) && options.stocks.length > 0) {
        // Synthesize point from active stocks
        let largeBookCr = 0;
        let midBookCr = 0;
        let smallBookCr = 0;
        let totalBookCr = 0;
        let crowdedBookCr = 0;

        for (const s of options.stocks) {
          const amt = s.amtCr || 0;
          totalBookCr += amt;
          if (s.capTier === 'large') largeBookCr += amt;
          else if (s.capTier === 'mid') midBookCr += amt;
          else smallBookCr += amt;
        }

        for (const s of options.stocks) {
          const amt = s.amtCr || 0;
          if (totalBookCr > 0 && (amt / totalBookCr) >= 0.01) {
            crowdedBookCr += amt;
          }
        }

        const largeBookPct = totalBookCr > 0 ? Math.round((largeBookCr / totalBookCr) * 10000) / 100 : 30.8;
        const midBookPct = totalBookCr > 0 ? Math.round((midBookCr / totalBookCr) * 10000) / 100 : 18.4;
        const smallBookPct = totalBookCr > 0 ? Math.round((smallBookCr / totalBookCr) * 10000) / 100 : 50.8;
        const crowdedBookPct = totalBookCr > 0 ? Math.round((crowdedBookCr / totalBookCr) * 10000) / 100 : 19.6;

        merged.push({
          date,
          largeBookPct,
          midBookPct,
          smallBookPct,
          largeMcapPct: prevPoint?.largeMcapPct ?? 59.52,
          midMcapPct: prevPoint?.midMcapPct ?? 20.26,
          smallMcapPct: prevPoint?.smallMcapPct ?? 20.22,
          crowdedBookPct,
          largeBookCr: Math.round(largeBookCr * 100) / 100,
          midBookCr: Math.round(midBookCr * 100) / 100,
          smallBookCr: Math.round(smallBookCr * 100) / 100,
          totalBookCr: Math.round(totalBookCr * 100) / 100,
          isDaily: true,
        });
      }
    }

    companySizeHistoryCache = merged;
    return merged;
  })();

  if (!options.forceFresh) {
    pendingCompanySizeHistoryFetch = request;
  }

  try {
    return await request;
  } finally {
    if (!options.forceFresh) {
      pendingCompanySizeHistoryFetch = null;
    }
  }
};

/**
 * Calculates current market size breakdown and compares it with AMFI market-cap share.
 */
export const calculateMtfCompanySizeBreakdown = (
  stocks: StockMtfItem[],
  sizeHistory: MtfSizeHistoryPoint[] = []
): MtfCompanySizeBreakdown => {
  let largeBookCr = 0;
  let largeCount = 0;
  let midBookCr = 0;
  let midCount = 0;
  let smallBookCr = 0;
  let smallCount = 0;
  let unmatchedBookCr = 0;
  let unmatchedCount = 0;

  for (const s of stocks) {
    const amt = s.amtCr || 0;
    if (s.capTier === 'large') {
      largeBookCr += amt;
      largeCount++;
    } else if (s.capTier === 'mid') {
      midBookCr += amt;
      midCount++;
    } else if (s.capTier === 'small') {
      smallBookCr += amt;
      smallCount++;
    } else {
      unmatchedBookCr += amt;
      unmatchedCount++;
    }
  }

  const computedTotalBookCr = Math.round((largeBookCr + midBookCr + smallBookCr + unmatchedBookCr) * 100) / 100;
  const latestHistory = sizeHistory.length > 0 ? sizeHistory[sizeHistory.length - 1] : null;

  const totalBookCr =
    latestHistory?.totalBookCr != null && computedTotalBookCr === 0
      ? latestHistory.totalBookCr
      : computedTotalBookCr;

  const finalLargeCr =
    latestHistory?.largeBookCr != null && computedTotalBookCr === 0
      ? latestHistory.largeBookCr
      : Math.round(largeBookCr * 100) / 100;

  const finalMidCr =
    latestHistory?.midBookCr != null && computedTotalBookCr === 0
      ? latestHistory.midBookCr
      : Math.round(midBookCr * 100) / 100;

  const finalSmallCr =
    latestHistory?.smallBookCr != null && computedTotalBookCr === 0
      ? latestHistory.smallBookCr
      : Math.round(smallBookCr * 100) / 100;

  const largeBookPct =
    latestHistory?.largeBookPct != null
      ? latestHistory.largeBookPct
      : totalBookCr > 0
      ? Math.round((largeBookCr / totalBookCr) * 10000) / 100
      : 30.81;
  const midBookPct =
    latestHistory?.midBookPct != null
      ? latestHistory.midBookPct
      : totalBookCr > 0
      ? Math.round((midBookCr / totalBookCr) * 10000) / 100
      : 17.31;
  const smallBookPct =
    latestHistory?.smallBookPct != null
      ? latestHistory.smallBookPct
      : totalBookCr > 0
      ? Math.round((smallBookCr / totalBookCr) * 10000) / 100
      : 48.15;
  const unmatchedBookPct =
    latestHistory != null
      ? 0
      : totalBookCr > 0
      ? Math.round((unmatchedBookCr / totalBookCr) * 10000) / 100
      : 0;

  return {
    asOfDate: latestHistory?.date || new Date().toISOString().slice(0, 10),
    totalBookCr,
    largeCap: {
      bookCr: finalLargeCr,
      bookPct: largeBookPct,
      mcapPct: latestHistory?.largeMcapPct ?? 60.46,
      stockCount: largeCount,
    },
    midCap: {
      bookCr: finalMidCr,
      bookPct: midBookPct,
      mcapPct: latestHistory?.midMcapPct ?? 20.31,
      stockCount: midCount,
    },
    smallCap: {
      bookCr: finalSmallCr,
      bookPct: smallBookPct,
      mcapPct: latestHistory?.smallMcapPct ?? 19.23,
      stockCount: smallCount,
    },
    unmatched: {
      bookCr: Math.round(unmatchedBookCr * 100) / 100,
      bookPct: unmatchedBookPct,
      stockCount: unmatchedCount,
    },
    history: sizeHistory,
  };
};

